import { Location, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from '@shared/services/toastr.service';
import { AuthService } from '@shared/services/auth.service';
import { Cluster } from '@shared/models/site';
import { OrganizationSiteService, SiteDetail } from '@shared/services/organization-site.service';
import { isEnum } from '@shared/utils/type-utils';
import { EditMode } from '@shared/edit.mode';
import { HostModeService } from '@shared/services/hostMode.service';
import { MySitesState } from '../my-sites/my-sites.component';
import { DirtyStateService } from '@shared/services/dirty-state/dirty-state.service';
import { UnsavedChangesService } from '@shared/services/unsaved-state/unsaved-changes.service';
import { HostModeLabelComponent } from '@shared/components/label/host-mode-label/host-mode-label.component';
import { SiteStatusFullComponent } from '@shared/components/site-status-full/site-status-full.component';
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';
import { GeneralConfigComponent } from './general-config/general-config.component';
import { CertificateErrorLabelComponent } from '@shared/components/label/certificate-error-label/certificate-error-label.component';
import { CertificatesComponent } from './certificates/certificates.component';
import { ExceptionsComponent } from './exceptions/exceptions.component';
import { GeoblockComponent } from './geoblock/geoblock.component';
import { RulesComponent } from './rules/rules.component';
import { RewriteRulesComponent } from './rewrite-rules/rewrite-rules.component';
import { CacheCdnComponent } from './cache-cdn/cache-cdn.component';
import { BrainOverridesComponent } from './brain-overrides/brain-overrides.component';
import { TranslatePipe } from '@ngx-translate/core';
import { cache, cache1 } from '@shared/utils/lang';
import { BrainParametersService, Visibility } from '@shared/services/brain-parameters.service';

@Component({
  selector: 'app-add-site',
  templateUrl: './add-site.component.html',
  styleUrls: ['./add-site.component.scss'],
  imports: [
    NgIf,
    HostModeLabelComponent,
    SiteStatusFullComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavContent,
    GeneralConfigComponent,
    CertificateErrorLabelComponent,
    CertificatesComponent,
    ExceptionsComponent,
    GeoblockComponent,
    RulesComponent,
    RewriteRulesComponent,
    CacheCdnComponent,
    BrainOverridesComponent,
    NgbNavOutlet,
    TranslatePipe,
  ],
})
export class AddSiteComponent implements OnInit, OnDestroy {
  editMode?: EditMode;
  site: SiteDetail;
  cluster: Cluster;
  activeTab: Tab = Tab.GENERAL;
  url: any = {};
  rules = [];
  isACertificateInError: boolean = false;
  cdnEnabled: boolean = false;
  mySitesState: MySitesState;
  protected readonly Tab = Tab;

  constructor(
    private readonly route: ActivatedRoute,
    protected readonly location: Location,
    private readonly siteApi: OrganizationSiteService,
    protected readonly brainParametersService: BrainParametersService,
    public readonly auth: AuthService,
    private readonly toastr: ToastrService,
    public readonly hostModeService: HostModeService,
    private readonly router: Router,
    private readonly dirtyState: DirtyStateService,
    private unsavedChanges: UnsavedChangesService,
  ) {
    this.route.data.subscribe(() => {
      const state = this.router?.getCurrentNavigation()?.extras?.state;
      this.mySitesState = state?.mySitesState;
    });
  }

  getBrainParametersGroups = cache(() => this.brainParametersService.getBrainParametersGroups());
  getBrainParameters = cache1((visiblity: Visibility) => this.siteApi.getBrainParameters(this.site.domainName, visiblity));

  ngOnInit() {
    const routeParams = this.route.snapshot.paramMap;
    const siteName = routeParams.get('siteName');
    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      this.activeTab = isEnum(Tab, tab) ? tab : Tab.GENERAL;
    });

    if (!siteName) {
      this.editMode = 'create';
    } else {
      this.editMode = 'update';
      this.loadSite(siteName);
    }
  }

  ngOnDestroy() {
    this.dirtyState.clear();
  }

  async goToTab(tab: Tab) {
    if (this.activeTab === tab) return;

    if (this.dirtyState.isDirty()) {
      const confirmed = await this.unsavedChanges.confirm({
        cancelKey: 'UnsavedChanges.Stay',
        confirmKey: 'QuitAndDiscard',
      });
      if (!confirmed) return;
      this.dirtyState.clear();
    }

    this.navigateToTab(tab);
  }

  private navigateToTab(tab: Tab) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
    });
  }

  loadSite(sitename: string) {
    return this.siteApi.getSite(sitename).subscribe({
      next: (site) => {
        this.site = site;
        this.isACertificateInError = site.isACertificateInError;
        this.cluster = this.site.cluster;
        this.cdnEnabled = this.cluster.supportedCdns.length > 0;
      },
      error: (error) => {
        console.error(error);
        this.toastr.error('Failed to load site');
      },
    });
  }

  // is called by children, so arrow function
  goBack = () => {
    this.router.navigate(['../'], { relativeTo: this.route, state: { mySitesState: this.mySitesState } });
  };

  refreshSite() {
    this.loadSite(this.site.domainName);
  }

  protected readonly cache = cache;
  protected readonly cache1 = cache1;
}

enum Tab {
  GENERAL = 'general',
  CERTIFICATES = 'certificates',
  EXCEPTIONS = 'exceptions',
  GEO_BLOCKING = 'geoBlocking',
  RULES = 'rules',
  REWRITE_RULES = 'rewriteRules',
  CACHE = 'cache',
  CACHE_CDN = 'cacheAndCdn',
  ADVANCED = 'advanced',
  EXPERT = 'expert',
}
